import "@assets/css/bootstrap-grid.css";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import { MentorFormData, MentorSchema } from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { MentorData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { toast } from "sonner";

const MentorForm: React.FC = () => {
  const queryClient = useQueryClient();
  const { searchParams, handleAction } = useHandleAction({});
  const { type } = useParams();
  const modalItem = searchParams.get("modal-item");
  const pathname = usePathname();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<MentorFormData>({
    mode: "onChange",
    resolver: zodResolver(MentorSchema),
    defaultValues: {
      first_name:
        modalItem === "add" ? (searchParams.get("search") as string) : "",
    },
  });
  console.log("🚀 ~ errors:", errors);
  console.log("Modle item", modalItem);

  /* set default value */
  const { setIsRetrive }: any = usePending();
  const { setIsFormPending } = useSelectionStore();

  const { data, isPending: fetchPending } = useListData<
    IndividualQuery<MentorData>
  >({
    endpoint: endpoint["mentor"],
    id: modalItem || "",
    key: "mentor" + modalItem,
    enabled: !!modalItem,
  });

  useEffect(() => {
    setIsRetrive(modalItem ? fetchPending : false);
    if (data) {
      if (searchParams.get("modal") == "add") {
        return reset({});
      } else {
        reset({
          about: data?.data?.about,
          image: data?.data?.image,
          doodle_image: data?.data?.doodle_image,
          first_name: data?.data?.user?.first_name,
          last_name: data?.data?.user?.last_name,
          email: data?.data?.user?.email,
          designation: [
            {
              label: data?.data?.designation?.["name"],
              value: data?.data?.designation?.["id"],
            },
          ],
          quote: data?.data?.quote || "",
        });
      }
    }
  }, [data, fetchPending]);

  const { mutate, isPending } = usePostData();
  console.log("mutation pending status out", isPending);
  /* muta te action */
  const handleMutate = (data: Partial<MentorFormData>) => {
    setIsFormPending(true);

    // @ts-ignore
    data = { ...data, designation: data["designation"][0]?.["value"] };

    if (searchParams.get("modal-item")) {
      data = {
        ...data,
        id: searchParams.get("modal-item"),
      };

      // if (!(data.image instanceof File || data.image instanceof Blob)) {
      //   delete data.image;
      // }
      // if (
      //   !(
      //     data.doodle_image instanceof File || data.doodle_image instanceof Blob
      //   )
      // ) {
      //   delete data.doodle_image;
      // }
    }

    return {
      data,
      endpoint: endpoint["mentor"],
      method: modalItem ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });
      setIsFormPending(false);
      handleAction({ type: "modal-close" });
      showToast({
        description: `${pathname} ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };

  const handleError = async (error: any) => {
    setIsFormPending(false);
    console.log("mutation pending status in", error?.data?.status);
    if (error?.data?.status == 413) {
      toast.error("File size too large");
    }

    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const onSubmit: SubmitHandler<MentorFormData> = (data) => {
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "first_name",
        label: "First Name",
        colSpan: 6,
        type: "text",
        placeholder: "Enter first name",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "last_name",
        label: "Last Name",
        colSpan: 6,
        type: "text",
        placeholder: "Enter last name",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "email",
        label: "Email",
        colSpan: 12,
        type: "text",
        placeholder: "Enter email",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "designation",
        label: "Designation",
        colSpan: 12,
        type: "async",
        placeholder: "Select Designation",
        endpoint: endpoint["designation"],
        settings: {
          add: true,
          update: true,
          remove: true,
          debounce: 1000,
        },
        format: { key: "name", value: "id", mutate: "name" },
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "about",
        label: "About Mentor",
        colSpan: 12,
        type: "editor",
        placeholder: "He/she is a ....",
        control: control,
        showLength: true,
        setValue: setValue,
        errors,
        className: ` `,
        visable: true,
      },
      {
        name: "quote",
        label: "Quote",
        colSpan: 12,
        type: "textarea",
        placeholder: "Inspirational quote from the mentor...",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "image",
        label: "Upload Image",
        colSpan: 6,
        type: "cropper",
        control: control,
        cropperWidthPlaceholder: 384,
        cropperHeightPlaceholder: 451,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
        upload_type: "image",
      },
      {
        name: "doodle_image",
        label: "Upload Doodle Image",
        colSpan: 6,
        cropperWidthPlaceholder: 384,
        cropperHeightPlaceholder: 451,
        type: "cropper",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
        upload_type: "image",
      },
      {
        type: "submit",
        value: `${searchParams.get("modal") || ""} ${(
          type || pathname
        )?.replace("-", " ")}`,
        className: "w-full h-[47px] mt-[32px] capitalize",
        colSpan: 12,
        variant: "default",

        endIcon: isPending ? <Spinner /> : null,
        disabled: isPending ? true : false,
        visable: true,
      },
    ],
    [control, setValue, errors, isPending]
  );

  if (fetchPending && modalItem)
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default MentorForm;
